import { useQuery } from '@tanstack/react-query';
import type { TaskStatus } from 'scheduler-types';
import { apiUrl, mockAuthHeaders } from '../lib/api';

export interface LineItemTask {
  id: string;
  laborBucket: string;
  status: TaskStatus;
  estimatedMinutesMin: number;
  estimatedMinutesMax: number;
  actualMinutes: number | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  assignedPersonId: string | null;
  assignedPersonName: string | null;
  isExpediteEligible: boolean;
  isPinned: boolean;
}

interface LineItemWithTasks {
  id: string;
  partNumber: string;
  quantity: number;
  bomId: string | null;
  schedulingStatus: string;
  tasks: LineItemTask[];
}

async function fetchLineItemTasks(lineItemId: string): Promise<LineItemTask[]> {
  const response = await fetch(apiUrl(`/api/v1/line-items/${lineItemId}`), {
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: { message: response.statusText },
    }));
    throw new Error(error.error?.message ?? `Failed to fetch line item: ${response.status}`);
  }

  const json = await response.json() as { data: LineItemWithTasks };
  return json.data?.tasks || [];
}

export function useLineItemTasks(lineItemId: string | undefined) {
  return useQuery({
    queryKey: ['lineItemTasks', lineItemId],
    queryFn: () => fetchLineItemTasks(lineItemId!),
    enabled: !!lineItemId,
    staleTime: 30000,
  });
}
